import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  TimelineItem,
  TimelineItemContent,
  TimelineItemTrigger,
  type TimelineItemTriggerProps,
} from '@rtkwlf/c3-recipes-timeline';
import { SpidrState, type Spidr } from '@rtkwlf/track-public';
import { Link } from 'react-router-dom';

import { routes } from '../../../../../constants/routes';
import { spidrStateToTimeline } from '../../../../../constants/spidr-state-to-appearance';
import { useSecurityJourneyTranslations } from '../../../../../locales/use-security-journey-translations';

import { SecurityFocusTimelineSpidrContent } from './security-focus-timeline-spidr-content';
import styles from './security-focus-timeline-spidr.module.css';

/* -------------------------------------------------------------------------------------------------
 * Utils
 * -----------------------------------------------------------------------------------------------*/
const getSpidrTriggerAppearance = (
  state: Spidr['state'],
): {
  color: TimelineItemTriggerProps['color'];
  icon: IconDefinition;
  label: TimelineItemTriggerProps['label'];
  size?: TimelineItemTriggerProps['size'];
  variant?: TimelineItemTriggerProps['variant'];
} => {
  const timelineItemAppearance =
    spidrStateToTimeline[state as keyof typeof spidrStateToTimeline];

  return {
    color: timelineItemAppearance.color,
    icon: timelineItemAppearance.icon,
    label: timelineItemAppearance.label,
  };
};

/* -------------------------------------------------------------------------------------------------
 * SecurityFocusTimelineSpidr
 * -----------------------------------------------------------------------------------------------*/
type SecurityFocusTimelineSpidrProps = {
  adhoc?: boolean;
  expiryDate: Spidr['expiryDate'];
  name: Spidr['blueprint']['name'];
  spidrID: Spidr['id'];
  state: Spidr['state'];
  ticketId: Spidr['ticketId'];
  focusID: string;
  tier: Spidr['blueprint']['tier'];
};

const SecurityFocusTimelineSpidr = ({
  adhoc = false,
  expiryDate,
  name,
  spidrID,
  state,
  ticketId,
  focusID,
  tier,
}: SecurityFocusTimelineSpidrProps) => {
  /**
   * Get an instance of translations
   */
  const { t } = useSecurityJourneyTranslations();

  /**
   * Get the timeline appearance
   */
  const timelineItemAppearance = getSpidrTriggerAppearance(state);

  const isSpidrDeliveredOrComplete =
    state === SpidrState.Delivered || state === SpidrState.Complete;

  return (
    <TimelineItem>
      <TimelineItemTrigger
        asChild={isSpidrDeliveredOrComplete}
        label={t(timelineItemAppearance.label)}
        color={timelineItemAppearance.color}
        size={timelineItemAppearance.size}
        variant={timelineItemAppearance.variant}
      >
        {isSpidrDeliveredOrComplete ? (
          <Link
            to={routes.securityFocuses.focusID.spidrs.spidrID.index({
              focusID,
              spidrID,
            })}
          >
            <FontAwesomeIcon icon={timelineItemAppearance.icon} />
          </Link>
        ) : (
          <FontAwesomeIcon icon={timelineItemAppearance.icon} />
        )}
      </TimelineItemTrigger>
      <TimelineItemContent
        className={styles['sft-spidr-item-content']}
        data-testid="security-focus-timeline-item-spidr"
      >
        <SecurityFocusTimelineSpidrContent
          adhoc={adhoc}
          expiryDate={expiryDate}
          name={name}
          state={state}
          ticketId={ticketId}
          tier={tier}
        />
      </TimelineItemContent>
    </TimelineItem>
  );
};

export { SecurityFocusTimelineSpidr };
export type { SecurityFocusTimelineSpidrProps };
